import { useState } from "react";
import { Loader2, Plus, X, Save, Trash2, CalendarDays } from "lucide-react";
import {
  useContenidos,
  useCreateContenido,
  useUpdateContenido,
  useDeleteContenido,
} from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";
import type { Contenido, ContenidoEstado, ContenidoFormData } from "../types";

const ESTADOS: { v: ContenidoEstado; label: string; cls: string }[] = [
  { v: "idea", label: "Idea", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  { v: "borrador", label: "Borrador", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { v: "revision", label: "Revisión", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  { v: "programado", label: "Programado", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  { v: "publicado", label: "Publicado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

const TIPOS = ["post", "articulo", "video", "anuncio"];

const EMPTY: ContenidoFormData = {
  titulo: "",
  tipo: "post",
  canal: "",
  estado: "idea",
  cuerpo: "",
  fecha_programada: "",
};

export function ContenidosPage() {
  const { emisorId } = useMarketingStore();
  const [estado, setEstado] = useState<string>("");
  const { data: contenidos, isLoading } = useContenidos(emisorId, { estado: estado || undefined });

  const createMut = useCreateContenido();
  const updateMut = useUpdateContenido();
  const deleteMut = useDeleteContenido();

  const [editing, setEditing] = useState<Contenido | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ContenidoFormData>(EMPTY);

  function openCreate() {
    setForm(EMPTY);
    setCreating(true);
    setEditing(null);
  }
  function openEdit(c: Contenido) {
    setForm({
      titulo: c.titulo,
      tipo: c.tipo,
      canal: c.canal ?? "",
      estado: c.estado,
      cuerpo: c.cuerpo ?? "",
      fecha_programada: c.fecha_programada ? c.fecha_programada.slice(0, 16) : "",
    });
    setEditing(c);
    setCreating(false);
  }
  function close() {
    setEditing(null);
    setCreating(false);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    const payload = { ...form, fecha_programada: form.fecha_programada || null };
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, payload });
    } else {
      await createMut.mutateAsync({ emisorId, payload });
    }
    close();
  }

  // Agrupar por estado para vista Kanban editorial
  const porEstado = (e: ContenidoEstado) => (contenidos ?? []).filter((c) => c.estado === e);

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <MarketingHeader
        title="Marketing"
        subtitle="Calendario editorial y contenidos."
        right={
          <button
            onClick={openCreate}
            disabled={!emisorId}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft transition self-end disabled:opacity-50"
          >
            <Plus size={16} /> Nuevo contenido
          </button>
        }
      />

      <div className="flex gap-2 shrink-0">
        <button
          onClick={() => setEstado("")}
          className={`rounded-lg px-3 py-1.5 text-sm font-bold ${estado === "" ? "bg-brand-700 text-white" : "bg-white border border-slate-200 text-slate-600"}`}
        >
          Todos
        </button>
        {ESTADOS.map((e) => (
          <button
            key={e.v}
            onClick={() => setEstado(e.v)}
            className={`rounded-lg px-3 py-1.5 text-sm font-bold ${estado === e.v ? "bg-brand-700 text-white" : "bg-white border border-slate-200 text-slate-600"}`}
          >
            {e.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {contenidos && (
        <div className="flex-1 min-h-0 overflow-x-auto">
          <div className="flex gap-3 h-full pb-2" style={{ minWidth: "max-content" }}>
            {ESTADOS.map((col) => {
              const items = porEstado(col.v);
              return (
                <div key={col.v} className="w-72 shrink-0 flex flex-col rounded-2xl border border-slate-200 bg-slate-50/60">
                  <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between">
                    <span className="font-bold text-sm text-slate-800">{col.label}</span>
                    <span className="text-xs text-slate-400">({items.length})</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[120px]">
                    {items.map((c) => (
                      <div
                        key={c.id}
                        className="group rounded-xl border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md transition cursor-pointer"
                        onClick={() => openEdit(c)}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="font-bold text-sm text-slate-900">{c.titulo}</div>
                          <button
                            onClick={(ev) => {
                              ev.stopPropagation();
                              if (confirm("¿Eliminar contenido?")) deleteMut.mutate(c.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-600 shrink-0"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          <span className="inline-flex px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold uppercase text-[10px]">
                            {c.tipo}
                          </span>
                          {c.canal && <span className="text-slate-500">{c.canal}</span>}
                        </div>
                        {c.fecha_programada && (
                          <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                            <CalendarDays size={11} />
                            {new Date(c.fecha_programada).toLocaleString("es-ES", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                    {items.length === 0 && (
                      <div className="text-center text-xs text-slate-400 py-6">Vacío</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-auto">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">
                {editing ? "Editar contenido" : "Nuevo contenido"}
              </h2>
              <button type="button" onClick={close} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </header>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Título *</label>
                <input required type="text" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tipo</label>
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Canal</label>
                <input type="text" value={form.canal ?? ""} onChange={(e) => setForm({ ...form, canal: e.target.value })} placeholder="instagram, linkedin, blog…" className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Estado</label>
                <select value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value as ContenidoEstado })} className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  {ESTADOS.map((e) => <option key={e.v} value={e.v}>{e.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Fecha programada</label>
                <input type="datetime-local" value={form.fecha_programada ?? ""} onChange={(e) => setForm({ ...form, fecha_programada: e.target.value })} className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cuerpo</label>
                <textarea rows={5} value={form.cuerpo ?? ""} onChange={(e) => setForm({ ...form, cuerpo: e.target.value })} className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50">Cancelar</button>
              <button type="submit" disabled={createMut.isPending || updateMut.isPending} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
                <Save size={14} /> Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
